import { CurrencyPipe, DatePipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { RegisterProjectIconsService } from '@app/finxone-web-frontend/app/lib/services/icon-service/register-project-icons.service';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { Locale } from '@finxone-platform/shared/enums';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  PostDownloadRedirectionConfig,
  SupportedDevice,
  ThemeConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import * as Sentry from '@sentry/angular';
import { KeycloakService } from 'keycloak-angular';
import { DeviceDetectorService, DeviceInfo, DeviceType } from 'ngx-device-detector';
import { Observable, Subject, map, take, takeUntil } from 'rxjs';
import { FontInjectorService } from '../../../../libs/shared/services/src/font-injector.service';
import { registerCustomIcons } from '../../../../libs/shared/utils/src/icons/register-icons.utils';
import { ConfigService } from './lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from './lib/services/countries-currencies-service/countries-currencies.service';
import { DataSourceService } from './lib/services/data-source-service/data-source.service';
import { DeviceTypeService } from './lib/services/device-type/device-type.service';
import { IdleTimeoutService } from './lib/services/idle-timeout-service/idle-timeout.service';
import { KeycloakWrapperService } from './lib/services/keycloak-wrapper-service/keycloak-wrapper.service';
import { SocketService } from './lib/services/socket-service/socket-service.service';
import { ProgressBarState } from './lib/state/progress-bar.state';
import { ProjectSettingsState } from './lib/state/project-settings.state';
import { isAuthenticatedRoute } from './lib/utils/auth-checks.utils';
import { logLoadEventToFlutter } from './lib/utils/flutter.utils';
import { registerHandlerbarHelpers } from './lib/utils/template/handlebar-helpers/handlerbar-helper-register.utils';
import { clearLocalStorageExcept } from './lib/utils/utils';
import { ViewContainerRefService } from '@app/finxone-web-frontend/app/lib/services/view-container-ref/view-container-ref.service';
import { RoleState } from './lib/state/role.state';

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  title = 'finxone-web-frontend';
  secondaryFontColour = '';
  fontFamily = '';
  fontFamilyUrl = '';
  thirdColour = '';
  secondaryFontFamily = '';
  deviceInfo: DeviceInfo;

  @Select(ProgressBarState.getProgressBarStack)
  progressBarStack: Observable<{ uniqueId?: string | number }[]>;

  private readonly postDownloadRedirectionEventName = 'postDownloadRedirectionEvent';

  private viewContainerRefService = inject(ViewContainerRefService);
  private viewContainerRef = inject(ViewContainerRef);

  constructor(
    private matIconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private configService: ConfigService,
    private deviceDetectorService: DeviceDetectorService,
    private deviceTypeService: DeviceTypeService,
    protected keycloakService: KeycloakService,
    protected keycloakWrapperService: KeycloakWrapperService,
    private datePipe: DatePipe,
    private currencyPipe: CurrencyPipe,
    private idleTimeoutService: IdleTimeoutService,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private store: Store,
    private iconCacheService: IconCacheService,
    private fontInjector: FontInjectorService,
    private registerProjectIconsService: RegisterProjectIconsService,
    private dataSourceService: DataSourceService,
    private router: Router,
    private socketService: SocketService,
  ) {}

  ngOnInit() {
    this.viewContainerRefService.set(this.viewContainerRef);
    this.deviceInfo = this.deviceDetectorService.getDeviceInfo();
    this.deviceTypeService.deviceType = this.deviceInfo.deviceType as SupportedDevice;

    /* TODO: Injecting custom fonts in the app component is a bit of an anti-pattern, we should look to move this logic somewhere else in the future.
    Also its not working, the fontFamily has been moved elsewhere in the theme.
    For now styles.scss is importing /assets/fonts/fonts.scss directly */
    this.configService.getTheme().subscribe((theme: ThemeConfig) => {
      Sentry.addBreadcrumb({
        category: 'Init',
        message: 'Setting up theming',
        level: 'info',
      });
      // load and inject custom fonts
      this.fontFamily = theme.fontFamily;
      this.fontInjector.injectFont(theme.fontFamily);
      this.secondaryFontFamily = theme.secondaryFontFamily ?? '';
      if (theme.secondaryFontFamily) this.fontInjector.injectFont(theme.secondaryFontFamily);
    });

    this.onLoadBfCache();
    Sentry.addBreadcrumb({
      category: 'Init',
      message: 'Initing main app component',
      level: 'info',
    });

    Sentry.addBreadcrumb({
      category: 'Init',
      message: 'Registering custom icons',
      level: 'info',
    });
    registerCustomIcons(this.matIconRegistry, this.sanitizer, this.iconCacheService);
    this.registerProjectIconsService.registerAllProjectIcons();

    Sentry.addBreadcrumb({
      category: 'Init',
      message: 'Registering handlebar helpers',
      level: 'info',
    });
    registerHandlerbarHelpers(
      this.datePipe,
      this.currencyPipe,
      this.countriesCurrenciesService,
      this.iconCacheService,
      this.store,
      this.dataSourceService,
      this.router,
      this.configService,
    );

    const locale = this.store.selectSnapshot(ProjectSettingsState.getProjectSettings)?.locale ?? Locale.en_GB;
    Sentry.addBreadcrumb({
      category: 'Init',
      message: 'Refreshing alerts based on locale',
      level: 'info',
    });
    this.configService.refreshAlerts(locale as Locale);
    this.configService.getLocaleVariableContent();

    document.addEventListener('touchstart', function () {}, true); // allow :active styles to work in Mobile Safari
    Sentry.addBreadcrumb({
      category: 'Init',
      message: 'Angular started to main mobile app',
      level: 'info',
    });
    logLoadEventToFlutter('angular-started');
    this.loggedInByBiometricEvent();
    this.setupPostDownloadRedirectionListener();
  }

  private onLoadBfCache() {
    this.configService
      .getFeatureFlags()
      .pipe(
        takeUntil(this.destroy$),
        map((response) => {
          if (!response?.bfCacheAlertDetection) {
            return;
          }

          window.addEventListener('pageshow', (event) => {
            if (event.persisted) {
              console.log('This page was restored from the bfcache.');
              const token = sessionStorage.getItem('token');
              /**
               * @description: checking if token is not there and user tries to login authenticated path then it wont go inside and it will redirect to login screen  .
               */
              if (!token && isAuthenticatedRoute(window.location.pathname)) {
                console.log(`AppComponent onLoadBfCache: clearLocalStorageExcept`);
                clearLocalStorageExcept([
                  'account',
                  'transactions',
                  'beneficiary',
                  'notifications',
                  'lastLoggedInId',
                  'APP_PREFERENCES',
                  'firstTime',
                  'lastSelectedOrg',
                ]);
                this.keycloakService.login({
                  redirectUri: window.location.href,
                });
              }
            } else {
              console.log('This page was loaded normally.');
            }
          });
        }),
      )
      .subscribe();
  }

  private biometricLoginEvent = () => {
    this.configService
      .getConfig()
      .pipe(
        take(1),
        map((config) => {
          console.log('called::refreshInit', config);
          this.keycloakWrapperService.refreshInit(config);
        }),
      )
      .subscribe();
  };

  // Event handler for post-download redirection event
  private postDownloadRedirectionEvent = () => {
    const postDownloadRedirectionConfig = this.store.selectSnapshot(ProjectSettingsState.getProjectSettings)
      ?.projectConfig?.postDownloadRedirectionConfig;

    if (!postDownloadRedirectionConfig) {
      console.warn('No post-download redirection configuration found.');
      return;
    }

    const selectedUrl = this.getPostDownloadRedirectionUrl(postDownloadRedirectionConfig);

    if (!selectedUrl) {
      console.warn('No URL configured for post-download redirection for the detected device type.');
      return;
    }

    console.log('Redirecting to URL:', selectedUrl);
    this.router.navigateByUrl(selectedUrl);
  };

  // Determine the appropriate redirection URL based on the device type
  private getPostDownloadRedirectionUrl(config: PostDownloadRedirectionConfig): string {
    let activeDevice = this.deviceTypeService?.deviceType;
    // Prefer the device held in role state over the detected device type: role state stores the device view
    // the workflow is actually rendered for (a tablet is resolved down to 'mobile' when Tablet View is
    // disabled for the project), so it's the one the redirection config is keyed by.
    const roleStateActiveDevice = this.store.selectSnapshot(RoleState.getRole)?.device;
    if (roleStateActiveDevice) {
      activeDevice = roleStateActiveDevice;
    }
    if (activeDevice === DeviceType.Tablet) {
      return config?.tabletUrl;
    }

    return config?.mobileUrl;
  }

  private loggedInByBiometricEvent() {
    window.addEventListener('loggedInByBiometric', this.biometricLoginEvent);
  }

  // Set up listener for post-download redirection events
  private setupPostDownloadRedirectionListener() {
    window.addEventListener(this.postDownloadRedirectionEventName, this.postDownloadRedirectionEvent);
  }

  private removeListener() {
    window.removeEventListener('loggedInByBiometric', this.biometricLoginEvent);
    window.removeEventListener(this.postDownloadRedirectionEventName, this.postDownloadRedirectionEvent);
  }

  ngOnDestroy(): void {
    this.removeListener();
  }
}
